import React from "react";
import type { CSSProperties } from "react";
import type { PostPreview } from "@/types";
import { Link } from "./link";
import { Image } from "./image";

type HomePostCardProps = {
  card: PostPreview;
  readMore: string;
  className?: string;
  style?: CSSProperties;
};

export const HomePostCard: React.FC<HomePostCardProps> = ({
  card,
  readMore,
  className,
  style,
}) => {
  // Match your existing routing shape
  const href = `/resources/${card.group}/${card.slug}`;

  // Title / image source as used in your PostCard
  const title = card.title || "Untitled";
  const imageSrc = card.coverImage; // same field your PostCard uses

  // Label + date formatting consistent with your PostCard
  const formatDate = (date: string) => {
    const d = new Date(date);
    if (isNaN(d.valueOf())) return "";
    const month = d.toLocaleString("default", { month: "long" });
    const day = d.getDate();
    return `${month} ${day}`;
  };

  const dateLabel = card.date ? formatDate(card.date) : "";

  return (
    <Link
      href={href}
      noStyle
      className={`postCard homePostCard ${className ? className : ""} postCard_light`}
      style={style}
    >
      <div className="homePostCard--media">
        {/* Use your custom Image component so images actually render */}
        <div className="postCard--imageWrapper">
          <Image
            src={imageSrc}
            alt={title || "Image"}
            className="postCard--image homePostCard--img"
            // quality/width/format as top-level props are not read by the
            // URL builder (getContentfulImage only looks at `options`), so
            // the old quality={85} silently fell back to the q=80 default.
            // q=60 webp is visually fine for these dimmed, text-overlaid
            // card thumbs and roughly halves their weight.
            options={{ quality: 60 }}
            // Cards are full-width on mobile, 2-up from 1024px.
            sizes="(min-width: 1024px) 50vw, 100vw"
            animateInView              // preserve your in-view fade/slide, if supported
          />
        </div>

        {/* Overlay: uses your existing postCard typography classes so fonts stay the same */}
        <div className="homePostCard--overlay">
          <div className="postCard--details">
            {card.label && <div className="postCard--tag">{card.label}</div>}
            {dateLabel && <div className="postCard--date">{dateLabel}</div>}
          </div>

          <h3 className="postCard--title">{title}</h3>

          <div className="postCard--readMore">{readMore}</div>
        </div>
      </div>
    </Link>
  );
};

export default HomePostCard;
